import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * FullBleed — a 100vw cinematic section with a background image, directional
 * scrim, subtle parallax on the image, and a content slot pinned to one side.
 *
 * Props:
 *   image         — background image URL
 *   imageMobile   — optional mobile crop
 *   alt           — accessible alt text
 *   height        — min height (Tailwind classes)
 *   side          — "left" | "right" | "center" content placement
 *   scrim         — "left" | "right" | "center" | "edges" scrim variant (matches side by default)
 *   redAccent     — show a soft red bloom
 *   children      — content
 *   testid        — data-testid for section
 */
export default function FullBleed({
  image,
  imageMobile,
  alt = "Cinematic automotive atmosphere — temporary imagery",
  height = "min-h-[92svh] md:min-h-[100svh]",
  side = "left",
  scrim,
  redAccent = false,
  objectPosition = "center",
  children,
  testid,
  className = "",
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);

  const scrimClass =
    (scrim || side) === "right" ? "scrim-right" :
    (scrim || side) === "center" ? "scrim-center" :
    (scrim || side) === "edges" ? "scrim-edges" : "scrim-left";

  const align =
    side === "right" ? "items-center justify-end text-right" :
    side === "center" ? "items-center justify-center text-center" :
    "items-center justify-start text-left";

  return (
    <section
      ref={ref}
      data-testid={testid}
      className={`relative w-screen ${height} overflow-hidden ${className}`}
      style={{ marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0 overflow-hidden">
        <picture className="block w-full h-full">
          {imageMobile && <source media="(max-width: 767px)" srcSet={imageMobile} />}
          <img
            src={image}
            alt={alt}
            className="block w-full h-full object-cover"
            style={{ objectPosition }}
            loading="lazy"
          />
        </picture>
      </motion.div>

      <div className={`absolute inset-0 z-[1] ${scrimClass}`} />
      {redAccent && <div className="absolute inset-0 z-[1] reflect-red opacity-80" />}

      {/* Seamless bottom fade into next black section */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 md:h-40 bg-gradient-to-b from-transparent to-ink-950 z-[2]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-ink-950/70 to-transparent z-[2]" />

      <div className={`relative z-10 wrap py-20 md:py-32 min-h-inherit flex ${align}`}>
        <div className="w-full md:w-[52%] max-w-2xl">
          {children}
        </div>
      </div>
    </section>
  );
}
